import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth-context';
import { ProjectProvider } from './lib/project-context';
import { ModulesProvider } from './lib/modules-context';
import { PreferencesProvider } from './lib/preferences-context';
import { queryClient, QueryClientProvider } from './lib/query-client';
import { ConfirmDialogProvider } from './components/ui/confirm-dialog';
import { AppLayout } from './components/AppLayout';
import { ModuleGate } from './components/ModuleSystem';
import BootGate from './components/BootGate';
import LoginPage from './pages/Login';
import RegisterPage from './pages/Register';
import DashboardPage from './pages/Dashboard';
import ProjectsPage from './pages/Projects';
import ProjectDetailPage from './pages/ProjectDetail';
import AnalyzerPage from './pages/Analyzer';
import ScenarioWizardPage from './pages/ScenarioWizard';
import SettingsPage from './pages/Settings';
import ProfilePage from './pages/Profile';
import HelpPage from './pages/Help';
import CopilotPage from './pages/Copilot';
import ReportsPage from './pages/Reports';
import CapitalStackPage from './pages/CapitalStack';
import ComingSoonPage from './pages/ComingSoon';
import MarketIntelligencePage from './pages/MarketIntelligence';
import ConstructionIntelligencePage from './pages/ConstructionIntelligence';
import PricingPage from './pages/Pricing';
import OnboardingPage from './pages/Onboarding';
import InstitutionalDashboardPage from './pages/InstitutionalDashboard';
import SuperAdminPage from './pages/SuperAdmin';
import './App.css';

function ProtectedRoute({ children, requireRole, blockRole }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center mx-auto mb-3" style={{background: 'var(--primary-hex)'}}>
            <svg className="h-4 w-4 text-white animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
          </div>
          <p className="text-sm text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireRole && user.role !== requireRole) {
    return <Navigate to="/" replace />;
  }

  // Platform super_admin has no tenant scope — redirect to /admin panel
  if (blockRole && user.role === blockRole) {
    return <Navigate to="/admin" replace />;
  }
  
  return (
    <ProjectProvider>
      <ModulesProvider>
        <PreferencesProvider>
          <AppLayout>{children}</AppLayout>
        </PreferencesProvider>
      </ModulesProvider>
    </ProjectProvider>
  );
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) return null;
  if (user) {
    // Check if user just registered → redirect to onboarding
    const onboardingRedirect = localStorage.getItem('onboarding_redirect');
    if (onboardingRedirect) {
      localStorage.removeItem('onboarding_redirect');
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/" replace />;
  }
  
  return children;
}

function OnboardingRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <BootGate>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ConfirmDialogProvider>
            <Routes>
            {/* Public routes */}
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/onboarding" element={<OnboardingRoute><OnboardingPage /></OnboardingRoute>} />
            
            {/* Protected routes */}
            <Route path="/" element={<ProtectedRoute blockRole="super_admin"><DashboardPage /></ProtectedRoute>} />
            <Route path="/projects" element={<ProtectedRoute blockRole="super_admin"><ProjectsPage /></ProtectedRoute>} />
            <Route path="/projects/:projectId" element={<ProtectedRoute blockRole="super_admin"><ProjectDetailPage /></ProtectedRoute>} />
            <Route path="/analyzer" element={<ProtectedRoute blockRole="super_admin"><ModuleGate module="financial"><AnalyzerPage /></ModuleGate></ProtectedRoute>} />
            <Route path="/analyzer/wizard/:projectId/:scenarioId" element={<ProtectedRoute blockRole="super_admin"><ModuleGate module="financial"><ScenarioWizardPage /></ModuleGate></ProtectedRoute>} />
            <Route path="/copilot" element={<ProtectedRoute blockRole="super_admin"><ModuleGate module="financial"><CopilotPage /></ModuleGate></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute blockRole="super_admin"><ModuleGate module="financial"><ReportsPage /></ModuleGate></ProtectedRoute>} />
            <Route path="/capital-stack" element={<ProtectedRoute blockRole="super_admin"><ModuleGate module="financial"><CapitalStackPage /></ModuleGate></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
            <Route path="/pricing" element={<ProtectedRoute><PricingPage /></ProtectedRoute>} />
            <Route path="/market" element={<ProtectedRoute blockRole="super_admin"><ModuleGate module="market"><MarketIntelligencePage /></ModuleGate></ProtectedRoute>} />
            <Route path="/construction" element={<ProtectedRoute blockRole="super_admin"><ModuleGate module="construction"><ConstructionIntelligencePage /></ModuleGate></ProtectedRoute>} />
            <Route path="/institutional" element={<ProtectedRoute blockRole="super_admin"><InstitutionalDashboardPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute requireRole="super_admin"><SuperAdminPage /></ProtectedRoute>} />
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </ConfirmDialogProvider>
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </BootGate>
  );
}

export default App;
